import { z } from 'zod';
import {
  AuditSchema,
  InstantSchema,
  MoneySchema,
  OrderIdSchema,
  PartyIdSchema,
  PlainDateSchema,
  ProductIdSchema,
  PurchaseTaskIdSchema,
  QuantitySchema,
  TenantScopedSchema,
  UserIdSchema,
  addQuantity,
  type Quantity,
} from './primitives.js';
import type { Order, OrderLine, OrderStatus } from './orders.js';

/* ------------------------------------------------------------------ *
 * Buying
 *
 * The buyer's screen is the one used at 04:00 on a phone, in a cold
 * hall, possibly with gloves on. It is the reason the app has a
 * `market` density and a dark theme that is not an afterthought.
 *
 * A shopping list is demand (all open order lines for a trading day)
 * collapsed per product, then split into tasks per supplier — because
 * the buyer walks the floor stand by stand, not product by product.
 * ------------------------------------------------------------------ */

export const ShoppingListSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: z.string().min(1),
  tradingDay: PlainDateSchema,
  status: z.enum(['open', 'in-progress', 'closed']).default('open'),
  /** Orders whose demand is rolled into this list. */
  orderIds: z.array(OrderIdSchema).default([]),
  buyerId: UserIdSchema.nullable().default(null),
  note: z.string().nullable().default(null),
});
export type ShoppingList = z.infer<typeof ShoppingListSchema>;

/**
 * One product to buy from one supplier. The buyer marks it bought,
 * short, or refused — and a short here propagates straight back to
 * every order line that asked for it, which is the whole point of
 * generating the list from demand rather than typing it.
 */
export const PurchaseTaskSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: PurchaseTaskIdSchema,
  shoppingListId: z.string().min(1),
  productId: ProductIdSchema,
  productName: z.string().min(1),
  sku: z.string().min(1),

  supplierId: PartyIdSchema.nullable().default(null),
  supplierName: z.string().nullable().default(null),
  /** Stand or gate on the market floor. The buyer navigates by these, not by supplier name. */
  standCode: z.string().nullable().default(null),

  /** Sum of open order demand. */
  required: QuantitySchema,
  /** Buffer the buyer adds on top for walk-ins and quality loss. */
  buffer: QuantitySchema.nullable().default(null),
  bought: QuantitySchema.nullable().default(null),

  /** What we expected to pay, from yesterday's quote. */
  expectedCost: MoneySchema.nullable().default(null),
  /** What was actually paid this morning. Feeds today's MarketQuote. */
  actualCost: MoneySchema.nullable().default(null),

  status: z.enum(['todo', 'bought', 'partial', 'unavailable', 'skipped']).default('todo'),
  /** Which orders are waiting on this line — shown so the buyer knows who is affected. */
  demandBreakdown: z
    .array(
      z.object({
        orderId: OrderIdSchema,
        orderNumber: z.string(),
        customerName: z.string(),
        quantity: QuantitySchema,
      }),
    )
    .default([]),

  note: z.string().nullable().default(null),
  completedAt: InstantSchema.nullable().default(null),
  sortOrder: z.number().int().default(0),
});
export type PurchaseTask = z.infer<typeof PurchaseTaskSchema>;

/**
 * A farm run. Direct-from-farm buying is a scheduled trip, not a
 * purchase order: you drive out on the days that farm harvests, and
 * what you come back with is what there was.
 */
export const SupplierRunSchema = AuditSchema.merge(TenantScopedSchema).extend({
  id: z.string().min(1),
  supplierId: PartyIdSchema,
  supplierName: z.string().min(1),
  date: PlainDateSchema,
  buyerId: UserIdSchema.nullable().default(null),
  vehicle: z.string().nullable().default(null),
  status: z.enum(['planned', 'departed', 'completed', 'cancelled']).default('planned'),
  taskIds: z.array(PurchaseTaskIdSchema).default([]),
  note: z.string().nullable().default(null),
});
export type SupplierRun = z.infer<typeof SupplierRunSchema>;

/** Demand for one product across all open orders on a trading day. */
export interface DemandLine {
  productId: string;
  productName: string;
  sku: string;
  required: import('./primitives.js').Quantity;
  /** How much of the requirement stock already covers. */
  onHand: import('./primitives.js').Quantity | null;
  toBuy: import('./primitives.js').Quantity;
  orderCount: number;
}

/* ------------------------------------------------------------------ *
 * The grouping board.
 *
 * Demand is every line of the day's confirmed orders, collapsed per
 * product and stamped with the supplier who is expected to cover it.
 * This is the sheet the buyer and each supplier read from: "the
 * restaurants ordered 36 kg of Raf between them; Valls brings it."
 *
 * Assignment is per line (an override recorded on the order), with the
 * product's default supplier as the fallback — so the board is derived,
 * never a second copy of the truth.
 * ------------------------------------------------------------------ */

/** Statuses whose lines still create buying demand. `ready` and later
    are already assembled; a draft is not yet a promise. */
export const DEMAND_ORDER_STATUSES: readonly OrderStatus[] = [
  'placed', 'sourcing', 'picking',
];

export interface DemandOrderRef {
  orderId: string;
  orderNumber: string;
  customerName: string;
  quantity: Quantity;
  status: OrderStatus;
}

/** One row of the grouping board: a product, its day total, and who covers it. */
export interface SupplierDemandLine {
  productId: string;
  productName: string;
  sku: string;
  total: Quantity;
  supplierId: string | null;
  supplierName: string | null;
  orders: DemandOrderRef[];
}

export interface SupplierDemandGroup {
  supplierId: string | null;
  supplierName: string | null;
  lines: SupplierDemandLine[];
  /** Distinct orders waiting on this supplier. */
  orderCount: number;
}

/** Units are added within a measure mode; if data disagrees on the mode
    (bad import, mixed catalog), the sum degrades to the first unit
    rather than throwing away the whole board. */
const safeAdd = (a: Quantity, b: Quantity): Quantity => {
  try {
    return addQuantity(a, b);
  } catch {
    return { value: a.value + b.value, unit: a.unit };
  }
};

/**
 * Collapse order lines into per-product demand rows.
 *
 * `resolveSupplier` answers "who covers this line" — the caller wires
 * in the fallback chain (line override → product default). Rows are
 * keyed by product *and* supplier, so a product split between two
 * suppliers shows as two honest rows, not a merged lie.
 */
export const buildSupplierDemand = (
  orders: readonly Pick<Order, 'id' | 'number' | 'customerName' | 'status' | 'lines'>[],
  resolveSupplier: (line: OrderLine) => { id: string; name: string } | null,
): SupplierDemandLine[] => {
  const rows = new Map<string, SupplierDemandLine>();

  for (const order of orders) {
    for (const line of order.lines) {
      const supplier = resolveSupplier(line);
      const key = `${line.productId}|${supplier?.id ?? ''}`;
      const existing = rows.get(key);
      const ref: DemandOrderRef = {
        orderId: order.id,
        orderNumber: order.number,
        customerName: order.customerName,
        quantity: line.ordered,
        status: order.status,
      };
      if (existing) {
        existing.total = safeAdd(existing.total, line.ordered);
        existing.orders.push(ref);
      } else {
        rows.set(key, {
          productId: line.productId,
          productName: line.productName,
          sku: line.sku,
          total: { ...line.ordered },
          supplierId: supplier?.id ?? null,
          supplierName: supplier?.name ?? null,
          orders: [ref],
        });
      }
    }
  }

  // Unassigned first — that is the work — then alphabetical by product.
  return [...rows.values()].sort((a, b) => {
    if ((a.supplierId === null) !== (b.supplierId === null)) {
      return a.supplierId === null ? -1 : 1;
    }
    return a.productName.localeCompare(b.productName);
  });
};

/** The same rows folded per supplier — the sheet each supplier reads. */
export const groupDemandBySupplier = (
  lines: readonly SupplierDemandLine[],
): SupplierDemandGroup[] => {
  const groups = new Map<string, SupplierDemandGroup>();
  for (const line of lines) {
    const key = line.supplierId ?? '';
    const group = groups.get(key) ?? {
      supplierId: line.supplierId,
      supplierName: line.supplierName,
      lines: [],
      orderCount: 0,
    };
    group.lines.push(line);
    groups.set(key, group);
  }
  for (const group of groups.values()) {
    group.orderCount = new Set(
      group.lines.flatMap((l) => l.orders.map((o) => o.orderId)),
    ).size;
  }
  // Unassigned first, then by name.
  return [...groups.values()].sort((a, b) => {
    if ((a.supplierId === null) !== (b.supplierId === null)) {
      return a.supplierId === null ? -1 : 1;
    }
    return (a.supplierName ?? '').localeCompare(b.supplierName ?? '');
  });
};
